import React, { createContext, useContext, useState, useEffect } from 'react';

const ServerContext = createContext();

// Lista de hooks/callbacks para invalidar cache quando servidor muda
const cacheInvalidationCallbacks = new Set();

export const useServer = () => {
  const context = useContext(ServerContext);
  if (!context) {
    throw new Error('useServer deve ser usado dentro de um ServerProvider');
  }
  return context;
};

export const ServerProvider = ({ children }) => {
  const [selectedServer, setSelectedServer] = useState('local');
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switchingServer, setSwitchingServer] = useState(false);

  // Carregar servidores disponíveis
  const fetchServers = async () => {
    try {
      const response = await fetch('/api/servers/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServers(data.servers || []);
        
        // Se o servidor selecionado não existir mais, selecionar o primeiro disponível
        const serverExists = data.servers.some(s => s.id === selectedServer);
        if (!serverExists && data.servers.length > 0) {
          setSelectedServer(data.servers[0].id);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar servidores:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServers();
  }, []);

  // Persistir servidor selecionado no localStorage
  useEffect(() => {
    localStorage.setItem('selectedServer', selectedServer);
  }, [selectedServer]);

  // Carregar servidor selecionado do localStorage na inicialização
  useEffect(() => {
    const saved = localStorage.getItem('selectedServer');
    if (saved) {
      setSelectedServer(saved);
    }
  }, []);

  const getCurrentServer = () => {
    return servers.find(s => s.id === selectedServer);
  };

  // Função para trocar servidor com invalidação de cache
  const switchServer = async (newServerId) => {
    if (newServerId === selectedServer) return;
    
    setSwitchingServer(true);
    
    try {
      // Invalidar todos os caches registrados
      cacheInvalidationCallbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.warn('Erro ao invalidar cache:', error);
        }
      });
      
      // Trocar servidor
      setSelectedServer(newServerId);
      
      // Pequeno delay para garantir que as requests antigas terminaram
      await new Promise(resolve => setTimeout(resolve, 100));
    } finally {
      setSwitchingServer(false);
    }
  };

  // Registrar callback para invalidação de cache
  const registerCacheInvalidation = (callback) => {
    cacheInvalidationCallbacks.add(callback);
    return () => cacheInvalidationCallbacks.delete(callback);
  };

  const value = {
    selectedServer,
    setSelectedServer: switchServer, // Usar a função que invalida cache
    servers,
    loading,
    switchingServer,
    fetchServers,
    getCurrentServer,
    registerCacheInvalidation
  };

  return (
    <ServerContext.Provider value={value}>
      {children}
    </ServerContext.Provider>
  );
};

export default ServerContext;